import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FornitoriAdmin } from 'src/app/models/supplier-portal/supplier/fornitori-admin.models';
import { FornitoriAdminMainService } from 'src/app/services/fornitori-admin-main.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export type TrackingStage = 1 | 2 | 3 | 4 | 5;

@Component({
  selector: 'app-fornitori-tracking',
  standalone: false,
  templateUrl: './fornitori-tracking.component.html',
  styleUrls: ['./fornitori-tracking.component.scss'],
})


export class FornitoriTrackingComponent implements OnInit {

  artQr = '';
  orderNumber = '';
  loading = false;
  ddtSafeUrl: SafeResourceUrl | null = null;
  history: FornitoriAdmin.TraceProductHistory | null = null;
  activeStage: TrackingStage = 1;
  selectedCard: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly svc: FornitoriAdminMainService,
    private readonly cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.artQr      = decodeURIComponent(this.route.snapshot.paramMap.get('qr') ?? '');
    this.orderNumber = this.route.snapshot.queryParamMap.get('order') ?? '';
    this.load();
  }

  private load(): void {
    if (!this.artQr) return;
    this.loading = true;
    this.svc.loadTraceProduct({ art_qr: this.artQr, order_number: this.orderNumber }).subscribe({
      next: (data) => {
        this.history = data;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/fornitori-tracciability']);
  }

  setStage(stage: TrackingStage): void {
    this.activeStage = stage;
    this.selectedCard = null;
  }

  toggleCard(card: string): void {
    this.selectedCard = this.selectedCard === card ? null : card;
    if (card === 'ddt' && this.history?.certification?.ddt_url) {
      this.ddtSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.history.certification.ddt_url
      );
    }
  }

  stageDate(stage: TrackingStage): string {
    if (!this.history) return '';
    switch (stage) {
      case 1: return this.formatDate(this.history.supplier?.order_date);
      case 2: return this.formatDate(this.history.certification?.start_date);
      case 3: return this.formatDate(this.history.warehouse?.start_date);
      case 4: return this.formatDate(this.history.expedition?.start_date);
      case 5: return this.formatDate(this.history.expedition?.expedition_date);
    }
  }

  stageLabel(stage: TrackingStage): string {
    switch (stage) {
      case 1: return 'Ordine al fornitore';
      case 2: return 'Certificazioni fornitore';
      case 3: return 'Carico in magazzino';
      case 4: return 'Preparazione ordine';
      case 5: return 'Status spedizione';
    }
  }

  formatDate(val: string | null | undefined): string {
    if (!val) return '-';
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  }

  formatDateTime(val: string | null | undefined): string {
    if (!val) return '-';
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} - ${hh}:${min}`;
  }

  get stages(): TrackingStage[] {
    return [1, 2, 3, 4, 5];
  }

  /**
   * Riepilogo validazione mostrato IN CIMA al pannello "Elenco
   * certificati articolo" (mock Figma: "I certificati sono stati
   * validati da NOME_CONTROLLER in data ... alle ore ..."), non per
   * singolo file. Usa il file validato più di recente come
   * rappresentante del gruppo (i certificati di un lotto vengono
   * validati insieme dallo stesso Quality Certifier).
   */
  get certValidationSummary(): { certifier: string; dateText: string } | null {
    const files = this.history?.certification?.files ?? [];
    const validated = files.filter((f) => f.check === 'S' || f.check === 'N');
    if (!validated.length) return null;
    const latest = validated.reduce((a, b) => {
      const da = a.check_date ? new Date(a.check_date).getTime() : 0;
      const db = b.check_date ? new Date(b.check_date).getTime() : 0;
      return db > da ? b : a;
    });
    return {
      certifier: latest.quality_certifier || '—',
      dateText: this.formatCertValidationDate(latest.check_date),
    };
  }

  formatCertValidationDate(val: string | null | undefined): string {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `in data ${dd}/${mm}/${yy} alle ore ${hh}:${min}`;
  }

  get ddtUrl(): string | null {
    return this.history?.certification?.ddt_url
      ?? this.history?.warehouse?.ddt_url
      ?? this.history?.expedition?.ddt_url
      ?? null;
  }

  openReport(): void {
    const payload = {
      art_qr: this.artQr,
      order_number: this.orderNumber
    };
    this.svc.getSupplierReport(payload).subscribe({
      next: (blob) => {
        const fileURL = URL.createObjectURL(blob);
        const win = window.open(fileURL, '_blank');

        if (!win) {
          const a = document.createElement('a');
          a.href = fileURL;
          a.download = `report-${payload.order_number}.pdf`;
          a.click();
        }
        setTimeout(() => URL.revokeObjectURL(fileURL), 2000);
      },
      error: (err) => {
        console.error('PDF generation failed', err);
      }
    });
  }

  openCertFile(url: string): void {
    window.open(url, '_blank');
  }
  
}
